import { AnimatePresence, motion } from "framer-motion";
import { Globe, X } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { springs } from "@/components/ui/motion";
import { useChatContext } from "@/stores/chat-context-store";
import { ENTITY_ICONS } from "./entity-icons";

export function ChatContextChips() {
  const { t } = useTranslation(["chat"]);
  const ambient = useChatContext((s) => s.ambient);
  const entities = useChatContext((s) => s.entities);
  const removeEntity = useChatContext((s) => s.removeEntity);

  const hasContent = ambient !== null || entities.length > 0;
  if (!hasContent) return null;

  return (
    <div className="flex flex-wrap gap-1.5 px-4 pt-2">
      <AnimatePresence mode="popLayout">
        {/* Ambient chip (page context, no remove button) */}
        {ambient ? (
          <motion.span
            key="ambient"
            className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-medium text-muted leading-tight"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={springs.smooth}
            aria-label={t("chat:context.currentPageLabel")}
          >
            <Globe size={12} className="shrink-0" />
            {ambient.label}
          </motion.span>
        ) : null}

        {/* Entity chips (with remove button) */}
        {entities.map((entity) => {
          const Icon = ENTITY_ICONS[entity.type];
          return (
            <motion.span
              key={entity.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 border border-accent/20 px-2.5 py-0.5 text-[11px] font-medium text-primary leading-tight"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={springs.smooth}
              layout
            >
              <Icon size={12} className="shrink-0" />
              <span className="max-w-[120px] truncate">{entity.label}</span>
              <button
                type="button"
                onClick={() => removeEntity(entity.id)}
                className="hover:opacity-70 transition-opacity ml-0.5 cursor-pointer"
                aria-label={t("chat:context.removeEntity", {
                  label: entity.label,
                })}
              >
                <X size={10} />
              </button>
            </motion.span>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
